import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  Package, Truck, Boxes, ClipboardList, Layers, RefreshCw, Hand, Box,
  Send, Undo2, Receipt, ShieldCheck, Users, Building2, Clock, Search,
  Sparkles, ChevronRight, X, Play, Code2, History, Plus, Info,
  Lightbulb, Command as CommandIcon, ArrowRight, Zap, Eye,
} from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/app/policies/prototype")({
  component: PolicyBuilderPrototype,
});

/* ────────────────────────────────────────────────────────────────────── */
/* Vocabulary registry                                                     */
/* ────────────────────────────────────────────────────────────────────── */

type Status = "live" | "soon";
type Tier = "default" | "more" | "advanced";

type Workflow = {
  id: string; label: string; icon: any; group: "inbound" | "inventory" | "outbound" | "operations" | "scope";
  tagline: string;
  teaches: string;
  capabilities: string[];
  triggers: { id: string; label: string; status: Status; tier: Tier; description: string }[];
  conditions: { id: string; label: string; type: "number" | "text" | "list" | "boolean"; status: Status; tier: Tier; unit?: string }[];
  actions: { id: string; label: string; status: Status; tier: Tier; params?: { name: string; label: string; type: string }[] }[];
};

const WORKFLOWS: Workflow[] = [
  { id: "orders", label: "Orders", icon: Package, group: "inbound",
    tagline: "Everything that happens between an order arriving and reaching the floor.",
    teaches: "Teach CairnOS how you triage, hold, route, and dress up orders before they ship.",
    capabilities: ["Order ingest", "Holds", "Waves", "Allocation", "Gifting & inserts", "Facility routing", "VIP handling"],
    triggers: [
      { id: "order.imported", label: "Order imported", status: "live", tier: "default", description: "Fires the moment an order lands in CairnOS from any channel." },
      { id: "order.updated", label: "Order updated", status: "live", tier: "default", description: "Fires when address, items, or shipping method change on an open order." },
      { id: "order.held", label: "Order held", status: "live", tier: "default", description: "Fires when an order enters On Hold status for any reason." },
      { id: "order.released", label: "Order released", status: "live", tier: "default", description: "Fires when a hold is cleared and the order re-enters fulfillment." },
      { id: "order.cancelled", label: "Order cancelled", status: "live", tier: "default", description: "Fires when an order is cancelled before shipment." },
      { id: "order.allocated", label: "Order allocated", status: "live", tier: "default", description: "Fires when inventory is reserved against the order." },
      { id: "wave.created", label: "Wave created", status: "soon", tier: "default", description: "Fires when a picking wave is generated." },
      { id: "wave.released", label: "Wave released", status: "soon", tier: "default", description: "Fires when a wave is released to the floor." },
    ],
    conditions: [
      { id: "order.subtotal", label: "Order subtotal", type: "number", status: "live", tier: "default", unit: "$" },
      { id: "order.total_items", label: "Total item quantity", type: "number", status: "live", tier: "default" },
      { id: "order.weight", label: "Order weight", type: "number", status: "live", tier: "default", unit: "lb" },
      { id: "order.contains_sku", label: "Order contains SKU", type: "list", status: "live", tier: "default" },
      { id: "order.is_subscription", label: "Subscription order", type: "boolean", status: "live", tier: "default" },
      { id: "order.is_first_time", label: "First-time customer", type: "boolean", status: "live", tier: "default" },
      { id: "order.has_hazmat", label: "Hazmat item present", type: "boolean", status: "live", tier: "default" },
      { id: "order.channel", label: "Sales channel", type: "text", status: "live", tier: "more" },
    ],
    actions: [
      { id: "hold_order", label: "Hold the order", status: "live", tier: "default", params: [{ name: "reason", label: "Reason", type: "text" }] },
      { id: "force_carrier", label: "Force carrier / method", status: "live", tier: "default", params: [{ name: "carrier", label: "Carrier", type: "text" }] },
      { id: "add_sku", label: "Add a free SKU (gift, insert)", status: "live", tier: "default", params: [{ name: "sku", label: "SKU", type: "text" }] },
      { id: "prevent_ship", label: "Prevent from shipping", status: "live", tier: "default", params: [{ name: "reason", label: "Reason", type: "text" }] },
      { id: "assign_facility", label: "Route to a different warehouse", status: "live", tier: "more", params: [{ name: "facility", label: "Facility", type: "text" }] },
    ],
  },
  { id: "receiving", label: "Receiving", icon: Truck, group: "inbound",
    tagline: "How trucks, ASNs, and blind receipts move from the dock into inventory.",
    teaches: "Teach CairnOS how strictly to check inbound freight and when to escalate variances.",
    capabilities: ["ASNs", "Blind receipts", "Variance tolerance", "Damage & QC", "Supplier trust", "Receiving charges"],
    triggers: [
      { id: "receipt.started", label: "Receipt started", status: "live", tier: "default", description: "Fires when a receiver begins checking in an ASN at the dock." },
      { id: "receipt.completed", label: "Receipt completed", status: "live", tier: "default", description: "Fires when a receiver marks an ASN complete and quantities post to inventory." },
      { id: "asn.received", label: "ASN received", status: "live", tier: "default", description: "Fires when an advance shipping notice arrives from a supplier." },
      { id: "receipt.variance", label: "Receipt variance detected", status: "live", tier: "default", description: "Fires when received quantities differ from the ASN by more than the tenant's tolerance." },
      { id: "receipt.damaged", label: "Damaged unit reported", status: "live", tier: "default", description: "Fires when a receiver flags damage during check-in." },
      { id: "receipt.blind", label: "Blind receipt closed", status: "soon", tier: "default", description: "Fires when a blind receipt is finalized." },
      { id: "receipt.approved", label: "Receipt approved", status: "soon", tier: "default", description: "Fires when a supervisor signs off on a held receipt." },
    ],
    conditions: [
      { id: "receipt.variance_pct", label: "Receipt variance", type: "number", status: "live", tier: "default", unit: "%" },
      { id: "receipt.line_count", label: "Receipt line count", type: "number", status: "live", tier: "default" },
      { id: "receipt.supplier", label: "Supplier / vendor", type: "text", status: "live", tier: "default" },
      { id: "receipt.damage_reported", label: "Damage reported", type: "boolean", status: "live", tier: "default" },
      { id: "receipt.blind", label: "Blind receipt", type: "boolean", status: "live", tier: "more" },
    ],
    actions: [
      { id: "hold_receipt", label: "Hold the receipt for review", status: "live", tier: "default", params: [{ name: "reason", label: "Reason", type: "text" }] },
      { id: "require_qc", label: "Require QC inspection", status: "live", tier: "default" },
      { id: "flag_variance", label: "Flag variance to supervisor", status: "live", tier: "default" },
      { id: "require_photo", label: "Require photo of damage", status: "live", tier: "default" },
      { id: "notify_purchasing", label: "Notify purchasing", status: "live", tier: "default" },
      { id: "auto_close", label: "Auto-close if within tolerance", status: "soon", tier: "more" },
    ],
  },
  { id: "putaway", label: "Putaway", icon: Boxes, group: "inbound",
    tagline: "Where product goes after it's received.",
    teaches: "Teach CairnOS how to route product to zones, enforce scans, and handle overrides.",
    capabilities: ["Suggested locations", "Zone routing", "Scan enforcement", "Overrides", "Cross-dock"],
    triggers: [
      { id: "putaway.suggested", label: "Putaway suggested", status: "live", tier: "default", description: "Fires when the system generates a recommended putaway location." },
      { id: "putaway.executed", label: "Putaway executed", status: "live", tier: "default", description: "Fires when a worker confirms stock in its putaway location." },
      { id: "putaway.exception", label: "Putaway exception", status: "live", tier: "default", description: "Fires when a worker deviates from the suggested location." },
      { id: "crossdock.triggered", label: "Cross-dock triggered", status: "soon", tier: "default", description: "Fires when inbound stock is routed directly to outbound." },
    ],
    conditions: [
      { id: "putaway.zone", label: "Target zone", type: "text", status: "live", tier: "default" },
      { id: "putaway.override", label: "Location override", type: "boolean", status: "live", tier: "default" },
    ],
    actions: [
      { id: "require_scan", label: "Require location scan", status: "live", tier: "default" },
      { id: "log_exception", label: "Log putaway exception", status: "live", tier: "default" },
    ],
  },
  { id: "inventory", label: "Inventory", icon: Layers, group: "inventory",
    tagline: "On-hand, thresholds, quarantine, lots, expirations.",
    teaches: "Teach CairnOS what \"low,\" \"stale,\" and \"expiring\" mean for your business.",
    capabilities: ["Thresholds", "Velocity class", "Lots & expiration", "Quarantine", "Days on hand", "Auto-PO drafts"],
    triggers: [
      { id: "inv.adjusted", label: "Inventory adjusted", status: "live", tier: "default", description: "Fires on any manual or system inventory adjustment." },
      { id: "inv.below_threshold", label: "Inventory below threshold", status: "live", tier: "default", description: "Fires when on-hand drops below a configured minimum." },
      { id: "inv.above_threshold", label: "Inventory above threshold", status: "live", tier: "default", description: "Fires when on-hand exceeds a configured maximum." },
      { id: "inv.moved", label: "Inventory moved", status: "live", tier: "default", description: "Fires when stock changes location within a facility." },
      { id: "inv.quarantined", label: "Inventory quarantined", status: "live", tier: "default", description: "Fires when stock is moved to quarantine status." },
      { id: "lot.created", label: "Lot created", status: "soon", tier: "default", description: "Fires when a new lot record is created." },
      { id: "lot.expiring", label: "Expiration approaching", status: "soon", tier: "default", description: "Fires N days before a lot's expiration date." },
    ],
    conditions: [
      { id: "inv.on_hand", label: "On-hand quantity", type: "number", status: "live", tier: "default" },
      { id: "inv.days_on_hand", label: "Days on hand", type: "number", status: "live", tier: "default" },
      { id: "inv.sku_class", label: "SKU velocity class", type: "text", status: "live", tier: "default" },
      { id: "inv.lot_age", label: "Lot age", type: "number", status: "soon", tier: "more", unit: "days" },
    ],
    actions: [
      { id: "alert_planner", label: "Alert the inventory planner", status: "live", tier: "default" },
      { id: "create_po_draft", label: "Create purchase order draft", status: "soon", tier: "default" },
    ],
  },
  { id: "cycle", label: "Cycle Counting", icon: ClipboardList, group: "inventory",
    tagline: "Blind counts, variance thresholds, recounts.",
    teaches: "Teach CairnOS how tolerant to be of counting variance and when to escalate.",
    capabilities: ["Blind counts", "Variance thresholds", "Recounts", "Supervisor escalation"],
    triggers: [
      { id: "cc.started", label: "Cycle count started", status: "live", tier: "default", description: "Fires when a cycle count session begins." },
      { id: "cc.completed", label: "Cycle count completed", status: "live", tier: "default", description: "Fires when a cycle count session is closed." },
      { id: "cc.variance", label: "Count variance detected", status: "live", tier: "default", description: "Fires when counted quantity differs from system." },
    ],
    conditions: [
      { id: "cc.variance_pct", label: "Count variance", type: "number", status: "live", tier: "default", unit: "%" },
      { id: "cc.blind", label: "Blind count", type: "boolean", status: "live", tier: "more" },
    ],
    actions: [
      { id: "recount", label: "Require a recount", status: "live", tier: "default" },
      { id: "escalate_variance", label: "Escalate to supervisor", status: "live", tier: "default" },
    ],
  },
  { id: "replenishment", label: "Replenishment", icon: RefreshCw, group: "inventory",
    tagline: "Pick face min/max, forward reserves, refill triggers.",
    teaches: "Teach CairnOS when the pick face needs refilling and from where.",
    capabilities: ["Pick face min/max", "Forward reserves", "Refill triggers", "Priority classes"],
    triggers: [
      { id: "repl.triggered", label: "Replenishment triggered", status: "soon", tier: "default", description: "Fires when a pick location falls below its min." },
      { id: "repl.executed", label: "Replenishment executed", status: "soon", tier: "default", description: "Fires when replen stock is delivered to the pick face." },
    ],
    conditions: [], actions: [],
  },
  { id: "picking", label: "Picking", icon: Hand, group: "outbound",
    tagline: "Assignment, shorts, exceptions, batch strategy.",
    teaches: "Teach CairnOS how to react when a picker can't fulfill or hits an exception.",
    capabilities: ["Assignment", "Batching", "Shorts", "Exceptions", "Reassignment"],
    triggers: [
      { id: "pick.assigned", label: "Pick assigned", status: "live", tier: "default", description: "Fires when a pick task is assigned to a worker." },
      { id: "pick.started", label: "Picking started", status: "live", tier: "default", description: "Fires when a worker begins a pick task." },
      { id: "pick.completed", label: "Pick completed", status: "live", tier: "default", description: "Fires when all lines on a pick task are picked." },
      { id: "pick.short", label: "Short pick", status: "live", tier: "default", description: "Fires when a picker cannot fulfill full quantity." },
      { id: "pick.exception", label: "Pick exception", status: "live", tier: "default", description: "Fires on any picker-reported issue." },
    ],
    conditions: [
      { id: "pick.short_qty", label: "Short quantity", type: "number", status: "live", tier: "default" },
      { id: "pick.worker", label: "Assigned worker", type: "text", status: "live", tier: "more" },
    ],
    actions: [
      { id: "reassign_pick", label: "Reassign the pick", status: "live", tier: "default" },
      { id: "escalate_short", label: "Escalate short pick", status: "live", tier: "default" },
    ],
  },
  { id: "packing", label: "Packing", icon: Box, group: "outbound",
    tagline: "Cartonization, weight checks, packing photos, inserts.",
    teaches: "Teach CairnOS what packing requires — photos, cartons, inserts, stickers.",
    capabilities: ["Cartonization", "Weight checks", "Packing photos", "Inserts & stickers", "Branded packaging"],
    triggers: [
      { id: "pack.started", label: "Packing started", status: "live", tier: "default", description: "Fires when a packer opens an order at a pack station." },
      { id: "pack.completed", label: "Packing completed", status: "live", tier: "default", description: "Fires when a packer confirms the last carton is closed." },
      { id: "pack.carton_closed", label: "Carton closed", status: "live", tier: "default", description: "Fires each time a carton is sealed and weighed." },
      { id: "pack.weight_exception", label: "Weight exception", status: "live", tier: "default", description: "Fires when actual weight differs from expected by tolerance." },
      { id: "pack.photo_captured", label: "Packing photo captured", status: "live", tier: "default", description: "Fires when a packer captures a required packing photo." },
    ],
    conditions: [
      { id: "pack.weight_diff", label: "Weight difference", type: "number", status: "live", tier: "default", unit: "%" },
      { id: "pack.carton_count", label: "Carton count", type: "number", status: "live", tier: "default" },
    ],
    actions: [
      { id: "require_photo", label: "Require a packing photo", status: "live", tier: "default" },
      { id: "force_packaging", label: "Force packaging type", status: "live", tier: "default", params: [{ name: "packaging", label: "Packaging", type: "text" }] },
      { id: "packing_instruction", label: "Add packing instruction", status: "live", tier: "default", params: [{ name: "text", label: "Instruction", type: "text" }] },
      { id: "add_sticker", label: "Add a sticker or insert", status: "live", tier: "default", params: [{ name: "asset", label: "Sticker / insert", type: "text" }] },
    ],
  },
  { id: "shipping", label: "Shipping", icon: Send, group: "outbound",
    tagline: "Rates, labels, cutoffs, commitments, carrier exceptions.",
    teaches: "Teach CairnOS when to upgrade service, hold, or notify around delivery risk.",
    capabilities: ["Rate shopping", "Service levels", "Cutoffs", "Delivery commitments", "Signature required", "Carrier exceptions"],
    triggers: [
      { id: "label.generated", label: "Label generated", status: "live", tier: "default", description: "Fires when a shipping label is purchased and printed." },
      { id: "shipment.created", label: "Shipment created", status: "live", tier: "default", description: "Fires when a shipment record is finalized." },
      { id: "manifest.closed", label: "Manifest closed", status: "live", tier: "default", description: "Fires when a daily manifest is closed with a carrier." },
      { id: "carrier.exception", label: "Carrier exception", status: "live", tier: "default", description: "Fires on any carrier-reported delivery issue." },
      { id: "tracking.received", label: "Tracking event received", status: "live", tier: "default", description: "Fires on any carrier tracking update." },
      { id: "delivery.confirmed", label: "Delivery confirmed", status: "live", tier: "default", description: "Fires when the carrier reports delivered." },
    ],
    conditions: [
      { id: "ship.carrier", label: "Carrier", type: "text", status: "live", tier: "default" },
      { id: "ship.service", label: "Service level", type: "text", status: "live", tier: "default" },
      { id: "ship.destination_state", label: "Destination state", type: "text", status: "live", tier: "default" },
      { id: "ship.destination_country", label: "Destination country", type: "text", status: "live", tier: "default" },
      { id: "ship.at_risk_edd", label: "Delivery date at risk", type: "boolean", status: "live", tier: "default" },
    ],
    actions: [
      { id: "upgrade_service", label: "Auto-upgrade service level", status: "live", tier: "default" },
      { id: "notify_customer", label: "Notify the customer", status: "live", tier: "default" },
      { id: "require_signature", label: "Require signature on delivery", status: "live", tier: "default" },
    ],
  },
  { id: "returns", label: "Returns", icon: Undo2, group: "outbound",
    tagline: "RMA lifecycle, grading, disposition, refunds.",
    teaches: "Teach CairnOS how to grade returns and route them to restock, refurb, or scrap.",
    capabilities: ["RMAs", "Grading", "Disposition", "Refunds", "Refurb queue"],
    triggers: [
      { id: "rma.opened", label: "RMA opened", status: "live", tier: "default", description: "Fires when a return authorization is created." },
      { id: "rma.received", label: "Return received", status: "live", tier: "default", description: "Fires when returned goods arrive at the facility." },
      { id: "rma.inspected", label: "Return inspected", status: "live", tier: "default", description: "Fires when a return is graded by a receiver." },
      { id: "rma.disposition", label: "Disposition set", status: "live", tier: "default", description: "Fires when a return is routed (restock, refurb, scrap)." },
    ],
    conditions: [], actions: [],
  },
  { id: "quality", label: "Quality", icon: ShieldCheck, group: "operations",
    tagline: "QC gates, holds, recalls.",
    teaches: "Teach CairnOS when to require inspection and how to isolate suspect stock.",
    capabilities: ["QC gates", "Holds", "Recalls", "Lot isolation"],
    triggers: [
      { id: "qc.started", label: "QC started", status: "live", tier: "default", description: "Fires when a QC inspection begins." },
      { id: "qc.passed", label: "QC passed", status: "live", tier: "default", description: "Fires when a QC inspection passes." },
      { id: "qc.failed", label: "QC failed", status: "live", tier: "default", description: "Fires when a QC inspection fails." },
      { id: "qc.recall", label: "Recall triggered", status: "soon", tier: "default", description: "Fires when a recall is opened against a lot or SKU." },
    ],
    conditions: [], actions: [],
  },
  { id: "labor", label: "Labor", icon: Users, group: "operations",
    tagline: "Shifts, assignments, idle detection, escalations.",
    teaches: "Teach CairnOS how to react to idle workers and reroute assignments.",
    capabilities: ["Shifts", "Assignments", "Idle detection", "Escalations"],
    triggers: [
      { id: "labor.shift_started", label: "Shift started", status: "live", tier: "default", description: "Fires when a worker clocks in." },
      { id: "labor.assigned", label: "Worker assigned", status: "live", tier: "default", description: "Fires when a task is assigned to a specific worker." },
      { id: "labor.idle", label: "Worker idle", status: "soon", tier: "default", description: "Fires after N minutes without task activity." },
      { id: "labor.escalated", label: "Task escalated", status: "soon", tier: "default", description: "Fires when a task escalates to a supervisor." },
    ],
    conditions: [], actions: [],
  },
  { id: "billing", label: "Billing", icon: Receipt, group: "operations",
    tagline: "3PL billable events, storage, minimums, invoices.",
    teaches: "Teach CairnOS how to price your operation — per pick, storage, pallet, minimum.",
    capabilities: ["Billable events", "Storage charges", "Pallet fees", "Monthly minimums", "Fee waivers", "Invoicing"],
    triggers: [
      { id: "billing.event", label: "Billable event created", status: "live", tier: "default", description: "Fires whenever a billable action posts to the ledger." },
      { id: "billing.period_close", label: "Billing period closed", status: "live", tier: "default", description: "Fires at the end of a billing cycle before invoicing." },
      { id: "billing.invoice", label: "Invoice generated", status: "live", tier: "default", description: "Fires when a customer invoice is finalized." },
    ],
    conditions: [
      { id: "billing.customer", label: "Customer", type: "text", status: "live", tier: "default" },
      { id: "billing.storage_days", label: "Storage days", type: "number", status: "live", tier: "default" },
      { id: "billing.amount", label: "Charge amount", type: "number", status: "live", tier: "default", unit: "$" },
    ],
    actions: [
      { id: "apply_charge", label: "Apply a charge", status: "live", tier: "default", params: [{ name: "amount", label: "Amount", type: "number" }, { name: "reason", label: "Reason", type: "text" }] },
      { id: "waive_fee", label: "Waive a fee", status: "live", tier: "default", params: [{ name: "reason", label: "Reason", type: "text" }] },
      { id: "generate_invoice", label: "Generate invoice", status: "live", tier: "more" },
    ],
  },
  { id: "warehouses", label: "Warehouses", icon: Building2, group: "scope",
    tagline: "Facility-wide capacity, dock schedules.",
    teaches: "Teach CairnOS how to react when a facility hits limits.",
    capabilities: ["Facility capacity", "Dock scheduling", "Multi-node routing"],
    triggers: [
      { id: "facility.capacity", label: "Facility capacity reached", status: "soon", tier: "default", description: "Fires when a facility hits configured capacity." },
      { id: "dock.assigned", label: "Dock door assigned", status: "soon", tier: "default", description: "Fires when a dock door is scheduled." },
    ],
    conditions: [], actions: [],
  },
  { id: "time", label: "Time & Scheduling", icon: Clock, group: "scope",
    tagline: "Cron-style triggers: daily, weekly, cutoffs, EOM.",
    teaches: "Teach CairnOS to act on a clock, not just events.",
    capabilities: ["Daily / weekly / monthly", "End of month", "Carrier cutoffs", "Holidays", "Shift boundaries"],
    triggers: [
      { id: "time.daily", label: "Daily", status: "live", tier: "default", description: "Fires once per day at the configured time." },
      { id: "time.weekly", label: "Weekly", status: "live", tier: "default", description: "Fires once per week on the configured day." },
      { id: "time.monthly", label: "Monthly", status: "live", tier: "default", description: "Fires once per month on the configured day." },
      { id: "time.eom", label: "End of month", status: "live", tier: "default", description: "Fires on the final business day of each month." },
      { id: "time.cutoff", label: "Carrier cutoff approaching", status: "live", tier: "default", description: "Fires N minutes before a carrier's daily pickup cutoff." },
      { id: "time.holiday", label: "Holiday", status: "soon", tier: "default", description: "Fires on tenant-configured holidays." },
      { id: "time.shift_change", label: "Shift change", status: "soon", tier: "default", description: "Fires at each shift boundary." },
    ],
    conditions: [], actions: [],
  },
];

const SHARED_CONDITIONS = [
  { id: "shared.customer", label: "Customer", type: "text" as const },
  { id: "shared.facility", label: "Facility", type: "text" as const },
  { id: "shared.tag", label: "Tag", type: "list" as const },
  { id: "shared.time_of_day", label: "Time of day", type: "text" as const },
  { id: "shared.role", label: "Role of actor", type: "text" as const },
];

const SHARED_ACTIONS = [
  { id: "shared.notify", label: "Send a notification" },
  { id: "shared.tag_entity", label: "Add a tag" },
  { id: "shared.assign", label: "Assign to someone" },
  { id: "shared.generate_doc", label: "Generate a document" },
  { id: "shared.webhook", label: "Send a webhook" },
];

const GROUPS: Record<string, { label: string; items: string[] }> = {
  inbound:    { label: "INBOUND", items: ["orders", "receiving", "putaway"] },
  inventory:  { label: "INVENTORY", items: ["inventory", "cycle", "replenishment"] },
  outbound:   { label: "OUTBOUND", items: ["picking", "packing", "shipping", "returns"] },
  operations: { label: "OPERATIONS", items: ["quality", "labor", "billing"] },
  scope:      { label: "SCOPE", items: ["warehouses", "time"] },
};

/* ────────────────────────────────────────────────────────────────────── */
/* Templates — organized library                                           */
/* ────────────────────────────────────────────────────────────────────── */

type Template = { title: string; category: string; workflow: string; popular?: boolean; recommended?: boolean; draft: PolicyDraft };

const TEMPLATES: Template[] = [
  // Receiving
  { title: "Hold receipt on > 2% variance", category: "Receiving", workflow: "receiving", popular: true, draft: {
    workflowId: "receiving", triggerId: "receipt.completed", triggerLabel: "Receipt completed",
    conditions: [{ workflowId: "receiving", conditionId: "receipt.variance_pct", label: "Receipt variance", op: "is greater than", value: "2", unit: "%" }],
    actions: [
      { workflowId: "receiving", actionId: "hold_receipt", label: "Hold the receipt for review", params: { reason: "QC review required" } },
      { workflowId: "receiving", actionId: "flag_variance", label: "Flag variance to supervisor", params: {} },
    ],
    unlessLabel: "the supplier is on the trusted list", duringLabel: "business hours",
  }},
  { title: "Require photos for damaged freight", category: "Receiving", workflow: "receiving", recommended: true, draft: {
    workflowId: "receiving", triggerId: "receipt.damaged", triggerLabel: "Damaged unit reported",
    conditions: [], actions: [{ workflowId: "receiving", actionId: "require_photo", label: "Require photo of damage", params: {} }],
    unlessLabel: null, duringLabel: null,
  }},
  { title: "Notify purchasing on blind receipts", category: "Receiving", workflow: "receiving", draft: {
    workflowId: "receiving", triggerId: "receipt.blind", triggerLabel: "Blind receipt closed",
    conditions: [], actions: [{ workflowId: "receiving", actionId: "notify_purchasing", label: "Notify purchasing", params: {} }],
    unlessLabel: null, duringLabel: null,
  }},
  { title: "Auto-close receipts within 0.5% tolerance", category: "Receiving", workflow: "receiving", draft: {
    workflowId: "receiving", triggerId: "receipt.completed", triggerLabel: "Receipt completed",
    conditions: [{ workflowId: "receiving", conditionId: "receipt.variance_pct", label: "Receipt variance", op: "is less than", value: "0.5", unit: "%" }],
    actions: [{ workflowId: "receiving", actionId: "auto_close", label: "Auto-close if within tolerance", params: {} }],
    unlessLabel: null, duringLabel: null,
  }},

  // Orders
  { title: "High-value order requires manager approval", category: "Orders", workflow: "orders", popular: true, draft: {
    workflowId: "orders", triggerId: "order.imported", triggerLabel: "Order imported",
    conditions: [{ workflowId: "orders", conditionId: "order.subtotal", label: "Order subtotal", op: "is greater than", value: "500", unit: "$" }],
    actions: [{ workflowId: "orders", actionId: "hold_order", label: "Hold the order", params: { reason: "Manager approval required" } }],
    unlessLabel: null, duringLabel: null,
  }},
  { title: "Add a free sticker on multi-item orders", category: "Customer Experience", workflow: "orders", recommended: true, draft: {
    workflowId: "orders", triggerId: "order.imported", triggerLabel: "Order imported",
    conditions: [{ workflowId: "orders", conditionId: "order.total_items", label: "Total item quantity", op: "is greater than", value: "1" }],
    actions: [{ workflowId: "packing", actionId: "add_sticker", label: "Add a sticker or insert", params: { asset: "Thank-you sticker" } }],
    unlessLabel: null, duringLabel: null,
  }},
  { title: "Welcome insert for first-time customers", category: "Customer Experience", workflow: "orders", draft: {
    workflowId: "orders", triggerId: "order.imported", triggerLabel: "Order imported",
    conditions: [{ workflowId: "orders", conditionId: "order.is_first_time", label: "First-time customer", op: "is true", value: "" }],
    actions: [{ workflowId: "packing", actionId: "add_sticker", label: "Add a sticker or insert", params: { asset: "Welcome card" } }],
    unlessLabel: null, duringLabel: null,
  }},
  { title: "Hazmat orders route to a certified facility", category: "Compliance", workflow: "orders", draft: {
    workflowId: "orders", triggerId: "order.imported", triggerLabel: "Order imported",
    conditions: [{ workflowId: "orders", conditionId: "order.has_hazmat", label: "Hazmat item present", op: "is true", value: "" }],
    actions: [{ workflowId: "orders", actionId: "assign_facility", label: "Route to a different warehouse", params: { facility: "OAK-1 (hazmat certified)" } }],
    unlessLabel: null, duringLabel: null,
  }},

  // Inventory
  { title: "Alert planner when velocity SKU drops under 25 units", category: "Inventory", workflow: "inventory", popular: true, draft: {
    workflowId: "inventory", triggerId: "inv.below_threshold", triggerLabel: "Inventory below threshold",
    conditions: [
      { workflowId: "inventory", conditionId: "inv.on_hand", label: "On-hand quantity", op: "is less than", value: "25" },
      { workflowId: "inventory", conditionId: "inv.sku_class", label: "SKU velocity class", op: "is", value: "A" },
    ],
    actions: [{ workflowId: "inventory", actionId: "alert_planner", label: "Alert the inventory planner", params: {} }],
    unlessLabel: null, duringLabel: null,
  }},
  { title: "Auto-draft PO for A-class SKUs at min", category: "Inventory", workflow: "inventory", recommended: true, draft: {
    workflowId: "inventory", triggerId: "inv.below_threshold", triggerLabel: "Inventory below threshold",
    conditions: [{ workflowId: "inventory", conditionId: "inv.sku_class", label: "SKU velocity class", op: "is", value: "A" }],
    actions: [{ workflowId: "inventory", actionId: "create_po_draft", label: "Create purchase order draft", params: {} }],
    unlessLabel: null, duringLabel: null,
  }},
  { title: "Quarantine lots within 30 days of expiration", category: "Inventory", workflow: "inventory", draft: {
    workflowId: "inventory", triggerId: "lot.expiring", triggerLabel: "Expiration approaching",
    conditions: [], actions: [{ workflowId: "inventory", actionId: "alert_planner", label: "Alert the inventory planner", params: {} }],
    unlessLabel: null, duringLabel: null,
  }},

  // Picking
  { title: "Escalate short picks to the picking supervisor", category: "Picking", workflow: "picking", popular: true, draft: {
    workflowId: "picking", triggerId: "pick.short", triggerLabel: "Short pick",
    conditions: [], actions: [{ workflowId: "picking", actionId: "escalate_short", label: "Escalate short pick", params: {} }],
    unlessLabel: null, duringLabel: null,
  }},
  { title: "Reassign picks when a worker is idle > 5 min", category: "Picking", workflow: "picking", draft: {
    workflowId: "labor", triggerId: "labor.idle", triggerLabel: "Worker idle",
    conditions: [], actions: [{ workflowId: "picking", actionId: "reassign_pick", label: "Reassign the pick", params: {} }],
    unlessLabel: null, duringLabel: null,
  }},

  // Packing
  { title: "Require photo on any order over 20 lbs", category: "Packing", workflow: "packing", popular: true, draft: {
    workflowId: "packing", triggerId: "pack.started", triggerLabel: "Packing started",
    conditions: [{ workflowId: "orders", conditionId: "order.weight", label: "Order weight", op: "is greater than", value: "20", unit: "lb" }],
    actions: [{ workflowId: "packing", actionId: "require_photo", label: "Require a packing photo", params: {} }],
    unlessLabel: null, duringLabel: null,
  }},
  { title: "Force branded box for subscription orders", category: "Packing", workflow: "packing", draft: {
    workflowId: "packing", triggerId: "pack.started", triggerLabel: "Packing started",
    conditions: [{ workflowId: "orders", conditionId: "order.is_subscription", label: "Subscription order", op: "is true", value: "" }],
    actions: [{ workflowId: "packing", actionId: "force_packaging", label: "Force packaging type", params: { packaging: "Branded mailer" } }],
    unlessLabel: null, duringLabel: null,
  }},
  { title: "Halt on > 15% weight variance", category: "Packing", workflow: "packing", draft: {
    workflowId: "packing", triggerId: "pack.weight_exception", triggerLabel: "Weight exception",
    conditions: [{ workflowId: "packing", conditionId: "pack.weight_diff", label: "Weight difference", op: "is greater than", value: "15", unit: "%" }],
    actions: [{ workflowId: "orders", actionId: "prevent_ship", label: "Prevent from shipping", params: { reason: "Weight variance exceeds tolerance" } }],
    unlessLabel: null, duringLabel: null,
  }},

  // Shipping
  { title: "Auto-upgrade shipping when delivery date at risk", category: "Shipping", workflow: "shipping", popular: true, draft: {
    workflowId: "shipping", triggerId: "label.generated", triggerLabel: "Label generated",
    conditions: [{ workflowId: "shipping", conditionId: "ship.at_risk_edd", label: "Delivery date at risk", op: "is true", value: "" }],
    actions: [{ workflowId: "shipping", actionId: "upgrade_service", label: "Auto-upgrade service level", params: {} }],
    unlessLabel: null, duringLabel: null,
  }},
  { title: "Require signature over $500", category: "Shipping", workflow: "shipping", recommended: true, draft: {
    workflowId: "shipping", triggerId: "label.generated", triggerLabel: "Label generated",
    conditions: [{ workflowId: "orders", conditionId: "order.subtotal", label: "Order subtotal", op: "is greater than", value: "500", unit: "$" }],
    actions: [{ workflowId: "shipping", actionId: "require_signature", label: "Require signature on delivery", params: {} }],
    unlessLabel: null, duringLabel: null,
  }},
  { title: "Notify customer success on carrier exceptions", category: "Shipping", workflow: "shipping", draft: {
    workflowId: "shipping", triggerId: "carrier.exception", triggerLabel: "Carrier exception",
    conditions: [], actions: [{ workflowId: "shipping", actionId: "notify_customer", label: "Notify the customer", params: {} }],
    unlessLabel: null, duringLabel: null,
  }},

  // Billing
  { title: "Charge storage after 30 days", category: "Billing", workflow: "billing", popular: true, draft: {
    workflowId: "billing", triggerId: "billing.period_close", triggerLabel: "Billing period closed",
    conditions: [{ workflowId: "billing", conditionId: "billing.storage_days", label: "Storage days", op: "is greater than", value: "30" }],
    actions: [{ workflowId: "billing", actionId: "apply_charge", label: "Apply a charge", params: { amount: "0.50", reason: "Long-term storage" } }],
    unlessLabel: null, duringLabel: null,
  }},
  { title: "Apply monthly minimum on active customers", category: "Billing", workflow: "billing", recommended: true, draft: {
    workflowId: "billing", triggerId: "billing.period_close", triggerLabel: "Billing period closed",
    conditions: [{ workflowId: "billing", conditionId: "billing.amount", label: "Charge amount", op: "is less than", value: "500", unit: "$" }],
    actions: [{ workflowId: "billing", actionId: "apply_charge", label: "Apply a charge", params: { amount: "500", reason: "Monthly minimum true-up" } }],
    unlessLabel: null, duringLabel: null,
  }},
  { title: "Bill pallet receiving at $8/pallet", category: "Billing", workflow: "billing", draft: {
    workflowId: "billing", triggerId: "billing.event", triggerLabel: "Billable event created",
    conditions: [], actions: [{ workflowId: "billing", actionId: "apply_charge", label: "Apply a charge", params: { amount: "8", reason: "Pallet receiving" } }],
    unlessLabel: null, duringLabel: null,
  }},

  // Quality
  { title: "Require QC on high-return-rate SKUs", category: "Quality", workflow: "quality", draft: {
    workflowId: "receiving", triggerId: "receipt.completed", triggerLabel: "Receipt completed",
    conditions: [], actions: [{ workflowId: "receiving", actionId: "require_qc", label: "Require QC inspection", params: {} }],
    unlessLabel: null, duringLabel: null,
  }},

  // Compliance
  { title: "Prevent shipping controlled SKUs to restricted states", category: "Compliance", workflow: "shipping", draft: {
    workflowId: "shipping", triggerId: "label.generated", triggerLabel: "Label generated",
    conditions: [{ workflowId: "shipping", conditionId: "ship.destination_state", label: "Destination state", op: "is one of", value: "CA, NY, MA" }],
    actions: [{ workflowId: "orders", actionId: "prevent_ship", label: "Prevent from shipping", params: { reason: "Restricted destination for controlled SKU" } }],
    unlessLabel: null, duringLabel: null,
  }},
];

const TEMPLATE_CATEGORIES = [
  "Most Popular", "Recommended",
  "Receiving", "Inventory", "Picking", "Packing", "Shipping", "Billing",
  "Customer Experience", "Quality", "Compliance", "Orders",
];

/* Common policies per workflow (learning surface) */
const COMMON_BY_WORKFLOW: Record<string, string[]> = {
  receiving: [
    "Hold receipts with > 2% variance",
    "Require photos for damaged freight",
    "Notify purchasing on blind receipts",
    "Auto-close receipts within tolerance",
  ],
  orders: [
    "Hold high-value orders for manager approval",
    "Route hazmat to certified facility",
    "Welcome insert for first-time customers",
    "Force expedited shipping for VIP customers",
  ],
  inventory: [
    "Alert planner below velocity-based threshold",
    "Auto-draft PO on A-class SKU at min",
    "Quarantine lots within 30 days of expiration",
    "Notify on stale slots > 45 days",
  ],
  picking: [
    "Escalate short picks to supervisor",
    "Reassign when picker is idle > 5 min",
    "Batch orders under 3 items for zone picking",
  ],
  packing: [
    "Require photo on orders over 20 lbs",
    "Force branded box for subscription orders",
    "Halt on > 15% weight variance",
    "Add insert on multi-item orders",
  ],
  shipping: [
    "Upgrade shipping when commitment is at risk",
    "Require signature over $500",
    "Notify customer success on delayed shipments",
    "Block shipping to restricted destinations",
  ],
  billing: [
    "Charge storage after 30 days",
    "Apply monthly minimum",
    "Bill pallet receiving",
    "Waive fees for anchor customers",
  ],
  cycle: [
    "Require recount on > 5% variance",
    "Escalate blind-count variance to supervisor",
  ],
  putaway: [
    "Require location scan on high-value SKUs",
    "Log every location override",
  ],
  quality: [
    "Require QC on high-return-rate SKUs",
    "Quarantine on failed QC",
  ],
  returns: [
    "Auto-refund on grade-A returns under $50",
    "Route damaged returns to refurb queue",
  ],
  labor: [
    "Reassign idle pickers",
    "Notify supervisor on escalations",
  ],
  replenishment: [
    "Trigger replen at pick-face min",
    "Prioritize A-class SKU replens",
  ],
  warehouses: ["Reroute orders when facility hits capacity"],
  time: ["Run daily storage charges at 6 AM", "Alert 30 min before carrier cutoff"],
};

/* Rotating example prompts */
const EXAMPLE_PROMPTS = [
  "Add a sticker to every Skura Style order with more than one item.",
  "Charge storage after 45 days.",
  "Require photos for damaged receipts.",
  "Upgrade shipping if delivery commitment is at risk.",
  "Automatically create a PO draft when A-class SKUs fall below 25 units.",
  "Hold any order over $500 for manager approval.",
  "Quarantine lots within 30 days of expiration.",
  "Escalate short picks to the picking supervisor.",
  "Waive the monthly minimum for anchor customers.",
  "Alert 30 minutes before the FedEx cutoff.",
];

/* ────────────────────────────────────────────────────────────────────── */
/* Prototype state                                                         */
/* ────────────────────────────────────────────────────────────────────── */

type Condition = { workflowId: string; conditionId: string; label: string; op: string; value: string; unit?: string };
type Action = { workflowId: string; actionId: string; label: string; params: Record<string, string> };
type PolicyDraft = {
  workflowId: string | null;
  triggerId: string | null;
  triggerLabel: string | null;
  conditions: Condition[];
  actions: Action[];
  unlessLabel: string | null;
  duringLabel: string | null;
};

const EMPTY: PolicyDraft = {
  workflowId: null, triggerId: null, triggerLabel: null,
  conditions: [], actions: [], unlessLabel: null, duringLabel: null,
};

/* ────────────────────────────────────────────────────────────────────── */
/* Natural-language → policy draft (prototype simulation)                  */
/* ────────────────────────────────────────────────────────────────────── */

function parseNaturalLanguage(prompt: string): PolicyDraft {
  const p = prompt.toLowerCase();
  const includes = (words: string[]) => words.some((w) => p.includes(w));

  // Workflow detection
  let workflowId = "orders";
  if (includes(["receipt", "receive", "receiving", "asn", "dock", "freight", "damaged", "variance", "blind receipt"])) workflowId = "receiving";
  else if (includes(["putaway", "put away", "location", "zone"])) workflowId = "putaway";
  else if (includes(["inventory", "stock", "on hand", "threshold", "expiration", "expiring", "quarantine", "po draft", "purchase order"])) workflowId = "inventory";
  else if (includes(["pick", "picking", "short pick", "picker"])) workflowId = "picking";
  else if (includes(["pack", "packing", "weight", "branded box", "packaging", "photo"])) workflowId = "packing";
  else if (includes(["ship", "shipping", "carrier", "delivery", "signature", "label", "rate shop", "upgrade"])) workflowId = "shipping";
  else if (includes(["return", "returns", "refurb", "refund"])) workflowId = "returns";
  else if (includes(["bill", "billing", "charge", "storage", "minimum", "monthly minimum"])) workflowId = "billing";
  else if (includes(["cycle count", "count"])) workflowId = "cycle";
  else if (includes(["quality", "qc", "inspection"])) workflowId = "quality";
  else if (includes(["labor", "worker", "idle", "picker"])) workflowId = "labor";

  const workflow = WORKFLOWS.find((w) => w.id === workflowId)!;
  const firstTrigger = workflow.triggers.find((t) => t.status === "live") ?? workflow.triggers[0];

  const draft: PolicyDraft = {
    workflowId,
    triggerId: firstTrigger?.id ?? null,
    triggerLabel: firstTrigger?.label ?? null,
    conditions: [],
    actions: [],
    unlessLabel: null,
    duringLabel: null,
  };

  // Conditions
  if (includes(["over $", "> $", "$", "dollar", "high-value", "high value"])) {
    const c = workflow.conditions.find((x) => x.id.includes("subtotal")) ?? workflow.conditions.find((x) => x.id.includes("amount"));
    const value = (p.match(/\$?\s*(\d+)/) || ["", "500"])[1];
    if (c) draft.conditions.push({ workflowId, conditionId: c.id, label: c.label, op: "is greater than", value, unit: c.unit });
  }
  if (includes(["variance", "difference", "tolerance", "%"])) {
    const c = workflow.conditions.find((x) => x.id.includes("variance"));
    const value = (p.match(/(\d+(?:\.\d+)?)\s*%/) || ["", "2"])[1];
    if (c) draft.conditions.push({ workflowId, conditionId: c.id, label: c.label, op: "is greater than", value, unit: "%" });
  }
  if (includes(["first time", "first-time", "new customer"])) {
    const c = workflow.conditions.find((x) => x.id.includes("first_time"));
    if (c) draft.conditions.push({ workflowId, conditionId: c.id, label: c.label, op: "is true", value: "" });
  }
  if (includes(["subscription"])) {
    const c = workflow.conditions.find((x) => x.id.includes("subscription"));
    if (c) draft.conditions.push({ workflowId, conditionId: c.id, label: c.label, op: "is true", value: "" });
  }
  if (includes(["hazmat"])) {
    const c = workflow.conditions.find((x) => x.id.includes("hazmat"));
    if (c) draft.conditions.push({ workflowId, conditionId: c.id, label: c.label, op: "is true", value: "" });
  }
  if (includes(["weight"])) {
    const c = workflow.conditions.find((x) => x.id.includes("weight"));
    const value = (p.match(/(\d+)\s*lb/) || ["", "20"])[1];
    if (c) draft.conditions.push({ workflowId, conditionId: c.id, label: c.label, op: "is greater than", value, unit: "lb" });
  }
  if (includes(["more than one item", "multi-item", "multiple items", "> 1 item"])) {
    const c = workflow.conditions.find((x) => x.id.includes("total_items"));
    if (c) draft.conditions.push({ workflowId, conditionId: c.id, label: c.label, op: "is greater than", value: "1" });
  }
  if (includes(["storage day", "after 30 days", "after 45 days", "days"])) {
    const c = workflow.conditions.find((x) => x.id.includes("storage_days") || x.id.includes("day"));
    const value = (p.match(/(\d+)\s*days?/) || ["", "30"])[1];
    if (c) draft.conditions.push({ workflowId, conditionId: c.id, label: c.label, op: "is greater than", value, unit: c.unit });
  }

  // Actions
  if (includes(["hold", "approval", "review"])) {
    const a = workflow.actions.find((x) => x.id.includes("hold")) ?? workflow.actions.find((x) => x.label.toLowerCase().includes("hold"));
    if (a) draft.actions.push({ workflowId, actionId: a.id, label: a.label, params: { reason: "Policy hold" } });
  }
  if (includes(["sticker", "insert", "gift", "welcome card", "free sku"])) {
    const a = workflow.actions.find((x) => x.id.includes("sticker") || x.id.includes("add_sku"));
    const asset = includes(["welcome card"]) ? "Welcome card" : includes(["sticker"]) ? "Thank-you sticker" : "Free insert";
    if (a) draft.actions.push({ workflowId, actionId: a.id, label: a.label, params: { asset } });
  }
  if (includes(["photo"])) {
    const a = workflow.actions.find((x) => x.id.includes("photo") || x.label.toLowerCase().includes("photo"));
    if (a) draft.actions.push({ workflowId, actionId: a.id, label: a.label, params: {} });
  }
  if (includes(["signature"])) {
    const a = workflow.actions.find((x) => x.id.includes("signature"));
    if (a) draft.actions.push({ workflowId, actionId: a.id, label: a.label, params: {} });
  }
  if (includes(["upgrade ship", "upgrade service", "upgrade shipping"])) {
    const a = workflow.actions.find((x) => x.id.includes("upgrade"));
    if (a) draft.actions.push({ workflowId, actionId: a.id, label: a.label, params: {} });
  }
  if (includes(["notify", "alert", "email"])) {
    const a = workflow.actions.find((x) => x.id.includes("notify") || x.label.toLowerCase().includes("notify") || x.label.toLowerCase().includes("alert"));
    if (a) draft.actions.push({ workflowId, actionId: a.id, label: a.label, params: {} });
  }
  if (includes(["charge", "bill"])) {
    const a = workflow.actions.find((x) => x.id.includes("charge") || x.label.toLowerCase().includes("charge"));
    const value = (p.match(/\$?\s*(\d+(?:\.\d+)?)/) || ["", "0.50"])[1];
    if (a) draft.actions.push({ workflowId, actionId: a.id, label: a.label, params: { amount: value, reason: "Policy charge" } });
  }
  if (includes(["prevent ship", "do not ship", "block ship"])) {
    const a = workflow.actions.find((x) => x.id.includes("prevent_ship"));
    if (a) draft.actions.push({ workflowId, actionId: a.id, label: a.label, params: { reason: "Policy block" } });
  }
  if (includes(["route", "facility"])) {
    const a = workflow.actions.find((x) => x.id.includes("assign_facility") || x.label.toLowerCase().includes("route"));
    if (a) draft.actions.push({ workflowId, actionId: a.id, label: a.label, params: { facility: "Auto-selected facility" } });
  }
  if (includes(["qc", "inspect"])) {
    const a = workflow.actions.find((x) => x.id.includes("require_qc") || x.label.toLowerCase().includes("qc"));
    if (a) draft.actions.push({ workflowId, actionId: a.id, label: a.label, params: {} });
  }
  if (includes(["escalate"])) {
    const a = workflow.actions.find((x) => x.id.includes("escalate") || x.label.toLowerCase().includes("escalate"));
    if (a) draft.actions.push({ workflowId, actionId: a.id, label: a.label, params: {} });
  }

  // Fallback: if nothing matched, add a placeholder action so the canvas opens.
  if (draft.actions.length === 0) {
    const a = workflow.actions.find((x) => x.status === "live") ?? workflow.actions[0];
    if (a) draft.actions.push({ workflowId, actionId: a.id, label: a.label, params: {} });
  }

  return draft;
}

function PolicyBuilderPrototype() {
  const [draft, setDraft] = useState<PolicyDraft>(EMPTY);
  const [activeWorkflow, setActiveWorkflow] = useState<string | null>(null); // null = home
  const [picker, setPicker] = useState<null | "when" | "if" | "then" | "unless" | "during">(null);
  const [advanced, setAdvanced] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const [search, setSearch] = useState("");
  const [commandOpen, setCommandOpen] = useState(false);
  const [parsing, setParsing] = useState(false);

  const started = draft.triggerId !== null;

  const handlePrompt = (prompt: string) => {
    if (!prompt.trim()) return;
    setParsing(true);
    setTimeout(() => {
      const parsed = parseNaturalLanguage(prompt);
      setDraft(parsed);
      setActiveWorkflow(parsed.workflowId);
      setParsing(false);
    }, 700);
  };

  // ⌘K / Ctrl-K to open command palette
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault(); setCommandOpen(true);
      }
      if (e.key === "Escape") setCommandOpen(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  return (
    <div className="h-[calc(100vh-3rem)] flex flex-col bg-background text-foreground">
      {/* Top bar */}
      <div className="h-12 border-b flex items-center px-4 gap-3 shrink-0">
        <button
          onClick={() => { setDraft(EMPTY); setActiveWorkflow(null); }}
          className="text-xs text-muted-foreground hover:text-foreground">
          ← Policies
        </button>
        <div className="text-sm font-medium">Create Policy</div>
        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 border border-amber-500/20">Prototype</span>

        <button
          onClick={() => setCommandOpen(true)}
          className="ml-4 hidden md:flex items-center gap-2 text-xs text-muted-foreground border rounded-md px-2.5 py-1 hover:bg-accent hover:text-foreground w-64">
          <Search className="h-3 w-3" />
          <span className="flex-1 text-left">Search anything — SKU, storage, hazmat…</span>
          <span className="text-[10px] font-mono border rounded px-1 py-0.5 bg-muted">⌘K</span>
        </button>

        <div className="ml-auto flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
            <input type="checkbox" checked={advanced} onChange={(e) => setAdvanced(e.target.checked)} className="accent-primary" />
            Advanced
          </label>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Draft</span>
          <button className="text-xs px-3 py-1.5 rounded bg-primary text-primary-foreground hover:opacity-90">Save policy</button>
        </div>
      </div>

      <div className={cn("flex-1 min-h-0 grid overflow-hidden",
        (activeWorkflow || started) ? "grid-cols-[240px_1fr]" : "grid-cols-1")}>
        {(activeWorkflow || started) && (
          <WorkflowSpine
            active={activeWorkflow}
            onSelect={(id) => { setActiveWorkflow(id); setDraft(EMPTY); }}
            onHome={() => { setActiveWorkflow(null); setDraft(EMPTY); }}
          />
        )}

        <div className="overflow-y-auto">
          <div className="max-w-5xl mx-auto p-8">
            {started ? (
              <>
                <SentenceCanvas
                  draft={draft}
                  onEdit={(clause) => setPicker(clause)}
                  onRemoveCondition={(i) => setDraft({ ...draft, conditions: draft.conditions.filter((_, idx) => idx !== i) })}
                  onRemoveAction={(i) => setDraft({ ...draft, actions: draft.actions.filter((_, idx) => idx !== i) })}
                  onClear={() => setDraft(EMPTY)}
                />
                <PreviewPanel draft={draft} />
                <ExplainabilityPanel draft={draft} />
                <FooterStrip />
                {advanced && showJson && <IrPanel draft={draft} />}
                {advanced && (
                  <div className="mt-3 flex gap-3 text-xs">
                    <button onClick={() => setShowJson((v) => !v)} className="text-muted-foreground hover:text-foreground flex items-center gap-1">
                      <Code2 className="h-3 w-3" /> {showJson ? "Hide" : "Show"} compiled primitives
                    </button>
                    <button className="text-muted-foreground hover:text-foreground flex items-center gap-1">
                      <History className="h-3 w-3" /> Simulate against 90 days
                    </button>
                  </div>
                )}
              </>
            ) : activeWorkflow ? (
              <WorkflowWorkspace
                workflow={WORKFLOWS.find((w) => w.id === activeWorkflow)!}
                onStartBlank={() => setPicker("when")}
                onTemplate={(t) => setDraft(t)}
                onOpenSearch={() => setCommandOpen(true)}
              />
            ) : (
              <HomeLanding
                onWorkflow={(id) => setActiveWorkflow(id)}
                onTemplate={(t) => setDraft(t)}
                onOpenSearch={() => setCommandOpen(true)}
                onSubmitPrompt={handlePrompt}
                parsing={parsing}
              />
            )}
          </div>
        </div>
      </div>

      {/* Pickers */}
      {picker === "when" && (
        <TriggerPicker
          workflowId={activeWorkflow ?? "orders"} setWorkflow={setActiveWorkflow}
          search={search} setSearch={setSearch}
          onPick={(wid: string, t: { id: string; label: string }) => {
            setDraft({ ...draft, workflowId: wid, triggerId: t.id, triggerLabel: t.label });
            setActiveWorkflow(wid);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
      {picker === "if" && (
        <ConditionPicker
          workflowId={draft.workflowId ?? activeWorkflow ?? "orders"}
          onPick={(c: Condition) => { setDraft({ ...draft, conditions: [...draft.conditions, c] }); setPicker(null); }}
          onClose={() => setPicker(null)}
        />
      )}
      {picker === "then" && (
        <ActionPicker
          workflowId={draft.workflowId ?? activeWorkflow ?? "orders"}
          onPick={(a: Action) => { setDraft({ ...draft, actions: [...draft.actions, a] }); setPicker(null); }}
          onClose={() => setPicker(null)}
        />
      )}
      {picker === "unless" && (
        <SimpleTextPicker title="UNLESS" placeholder="e.g. the supplier is on the trusted list"
          onPick={(v: string) => { setDraft({ ...draft, unlessLabel: v }); setPicker(null); }}
          onClose={() => setPicker(null)} />
      )}
      {picker === "during" && (
        <SimpleTextPicker title="DURING" placeholder="e.g. business hours (Mon–Fri, 8am–6pm)"
          onPick={(v: string) => { setDraft({ ...draft, duringLabel: v }); setPicker(null); }}
          onClose={() => setPicker(null)} />
      )}

      {commandOpen && (
        <CommandPalette
          onClose={() => setCommandOpen(false)}
          onWorkflow={(id) => { setActiveWorkflow(id); setDraft(EMPTY); setCommandOpen(false); }}
          onTemplate={(t) => { setDraft(t); setCommandOpen(false); }}
          onTrigger={(wid, t) => {
            setDraft({ ...EMPTY, workflowId: wid, triggerId: t.id, triggerLabel: t.label });
            setActiveWorkflow(wid); setCommandOpen(false);
          }}
        />
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Workflow spine                                                          */
/* ────────────────────────────────────────────────────────────────────── */

function WorkflowSpine({ active, onSelect, onHome }: { active: string | null; onSelect: (id: string) => void; onHome: () => void }) {
  return (
    <aside className="border-r overflow-y-auto bg-muted/20">
      <button onClick={onHome}
        className={cn("w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent border-b",
          active === null && "bg-accent")}>
        <Sparkles className="h-3.5 w-3.5 text-primary" />
        <span className="flex-1 text-left font-medium">All workflows</span>
      </button>
      {Object.values(GROUPS).map((grp) => (
        <div key={grp.label} className="pb-2">
          <div className="px-3 pt-3 pb-1 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/70">{grp.label}</div>
          {grp.items.map((wid) => {
            const w = WORKFLOWS.find((x) => x.id === wid)!;
            const Icon = w.icon;
            const isActive = active === w.id;
            return (
              <button key={w.id} onClick={() => onSelect(w.id)}
                className={cn("w-full flex items-center gap-2 pl-3 pr-3 py-1.5 text-sm hover:bg-accent group relative",
                  isActive && "bg-accent text-accent-foreground font-medium")}>
                {isActive && <span className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary" />}
                <Icon className={cn("h-3.5 w-3.5", isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground")} />
                <span className="flex-1 text-left">{w.label}</span>
              </button>
            );
          })}
        </div>
      ))}
    </aside>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Home landing — energy + AI prompt + templates                           */
/* ────────────────────────────────────────────────────────────────────── */

function HomeLanding({ onWorkflow, onTemplate, onOpenSearch, onSubmitPrompt, parsing }: {
  onWorkflow: (id: string) => void;
  onTemplate: (t: PolicyDraft) => void;
  onOpenSearch: () => void;
  onSubmitPrompt: (prompt: string) => void;
  parsing: boolean;
}) {
  const [promptIdx, setPromptIdx] = useState(0);
  const [prompt, setPrompt] = useState("");
  useEffect(() => {
    const t = setInterval(() => setPromptIdx((i) => (i + 1) % EXAMPLE_PROMPTS.length), 3500);
    return () => clearInterval(t);
  }, []);

  const submit = () => {
    onSubmitPrompt(prompt);
    setPrompt("");
  };

  const [cat, setCat] = useState<string>("Most Popular");
  const templatesFor = (c: string) => {
    if (c === "Most Popular") return TEMPLATES.filter((t) => t.popular);
    if (c === "Recommended") return TEMPLATES.filter((t) => t.recommended);
    return TEMPLATES.filter((t) => t.category === c);
  };

  return (
    <div className="space-y-10 relative">
      {parsing && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-background/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span>CairnOS is drafting this rule for you…</span>
          </div>
        </div>
      )}
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-primary/5 via-card to-card p-10">
        <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-primary/5 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-primary/80 bg-primary/10 border border-primary/20 rounded-full px-2 py-0.5 mb-4">
            <Zap className="h-3 w-3" /> Teach CairnOS how your warehouse operates
          </div>
          <h1 className="text-4xl font-semibold tracking-tight leading-tight">
            What should your warehouse do?
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Describe a rule in plain English, pick a workflow, or start from one of {TEMPLATES.length}+ policies real warehouses run today.
          </p>

          <div className="mt-6 max-w-2xl">
            <div className="relative">
              <Sparkles className="absolute left-3.5 top-3.5 h-4 w-4 text-primary" />
              <input
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && prompt.trim()) submit();
                }}
                disabled={parsing}
                className="w-full pl-10 pr-24 py-3.5 rounded-xl border bg-card/80 backdrop-blur text-sm outline-none focus:ring-2 focus:ring-primary/40 shadow-sm disabled:opacity-60"
                placeholder={EXAMPLE_PROMPTS[promptIdx]}
              />
              <button
                onClick={submit}
                disabled={!prompt.trim() || parsing}
                className="absolute right-1.5 top-1.5 bottom-1.5 px-3 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
              >
                Draft rule
              </button>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-2 pl-1">
              <Lightbulb className="h-3 w-3" />
              <span>Try: <span className="text-foreground/80 italic">"{EXAMPLE_PROMPTS[(promptIdx + 1) % EXAMPLE_PROMPTS.length]}"</span></span>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2 text-xs">
            <button onClick={onOpenSearch}
              className="inline-flex items-center gap-1.5 border rounded-md px-2.5 py-1.5 hover:bg-accent">
              <CommandIcon className="h-3 w-3" /> Search vocabulary
              <span className="text-[10px] font-mono border rounded px-1 py-0.5 bg-muted ml-1">⌘K</span>
            </button>
            <span className="text-muted-foreground">or</span>
            <span className="text-muted-foreground">choose a place in your warehouse below.</span>
          </div>
        </div>
      </div>

      {/* Workflows as places */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Choose a part of your warehouse to teach</h2>
          <span className="text-xs text-muted-foreground">{WORKFLOWS.length} workflows · {TEMPLATES.length} templates</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {WORKFLOWS.map((w) => {
            const Icon = w.icon;
            const tplCount = TEMPLATES.filter((t) => t.workflow === w.id).length;
            const caps = w.capabilities ?? [];
            return (
              <button key={w.id} onClick={() => onWorkflow(w.id)}
                className="group text-left rounded-xl border bg-card p-4 hover:border-primary/40 hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col">
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon className="h-4.5 w-4.5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{w.label}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground/80">{w.group}</div>
                  </div>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-3">{w.tagline}</div>
                {caps.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {caps.slice(0, 5).map((c) => (
                      <span key={c} className="text-[10px] px-1.5 py-0.5 rounded-md bg-muted/60 text-foreground/80 border border-border/60">
                        {c}
                      </span>
                    ))}
                    {caps.length > 5 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-md text-muted-foreground">
                        +{caps.length - 5} more
                      </span>
                    )}
                  </div>
                )}
                <div className="mt-auto pt-2 border-t border-border/60 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>
                    <span className="text-foreground font-medium tabular-nums">{tplCount}</span> template{tplCount === 1 ? "" : "s"}
                  </span>
                  <span className="text-muted-foreground/70 group-hover:text-primary transition-colors">Teach CairnOS →</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Templates library */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Policies real warehouses run</h2>
          <span className="text-xs text-muted-foreground">{TEMPLATES.length} templates</span>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {TEMPLATE_CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCat(c)}
              className={cn("text-xs px-2.5 py-1 rounded-full border",
                cat === c ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-accent text-muted-foreground")}>
              {c}
            </button>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          {templatesFor(cat).map((t) => (
            <button key={t.title} onClick={() => onTemplate(t.draft)}
              className="group flex items-start gap-3 px-3 py-2.5 rounded-lg border bg-card text-sm hover:border-primary/40 text-left">
              <div className="h-7 w-7 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                <ChevronRight className="h-3.5 w-3.5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{t.title}</div>
                <div className="text-[11px] text-muted-foreground">{t.category}</div>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground group-hover:text-primary self-center">Use →</span>
            </button>
          ))}
          {templatesFor(cat).length === 0 && (
            <div className="col-span-full text-xs text-muted-foreground italic p-6 border rounded-lg text-center">
              More {cat} templates coming as the library grows.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Workflow workspace — "you entered a place"                              */
/* ────────────────────────────────────────────────────────────────────── */

function WorkflowWorkspace({ workflow, onStartBlank, onTemplate, onOpenSearch }: {
  workflow: Workflow; onStartBlank: () => void; onTemplate: (t: PolicyDraft) => void; onOpenSearch: () => void;
}) {
  const Icon = workflow.icon;
  const common = COMMON_BY_WORKFLOW[workflow.id] ?? [];
  const templates = TEMPLATES.filter((t) => t.workflow === workflow.id);
  const liveTriggers = workflow.triggers.filter((t) => t.status === "live").length;
  const liveConditions = workflow.conditions.filter((c) => c.status === "live").length;
  const liveActions = workflow.actions.filter((a) => a.status === "live").length;

  return (
    <div className="space-y-8">
      {/* Workspace header */}
      <div className="rounded-2xl border bg-gradient-to-br from-primary/5 via-card to-card p-6 flex items-start gap-4">
        <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Icon className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-primary/80 mb-0.5">You entered</div>
          <h1 className="text-2xl font-semibold tracking-tight">{workflow.label}</h1>
          <p className="text-sm text-muted-foreground mt-1">{workflow.tagline}</p>
          <p className="text-xs text-foreground/70 mt-2 italic">{workflow.teaches}</p>
          <div className="flex flex-wrap gap-3 mt-4 text-[11px] text-muted-foreground">
            <VocabTag label="Events" count={liveTriggers} />
            <VocabTag label="Conditions" count={liveConditions} />
            <VocabTag label="Actions" count={liveActions} />
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button onClick={onStartBlank}
            className="text-xs px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 inline-flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Start a blank policy
          </button>
          <button onClick={onOpenSearch}
            className="text-xs px-3 py-2 rounded-lg border hover:bg-accent inline-flex items-center gap-1.5 justify-center">
            <Search className="h-3 w-3" /> Search this workflow
          </button>
        </div>
      </div>

      {/* Common policies — learning surface */}
      {common.length > 0 && (
        <div>
          <div className="flex items-baseline gap-2 mb-2">
            <Lightbulb className="h-3.5 w-3.5 text-amber-500" />
            <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
              Common policies warehouses create in {workflow.label}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            {common.map((c) => {
              const matched = TEMPLATES.find((t) => t.title.toLowerCase().startsWith(c.toLowerCase().slice(0, 20)));
              return (
                <button key={c}
                  onClick={() => matched ? onTemplate(matched.draft) : onStartBlank()}
                  className="flex items-start gap-2 px-3 py-2.5 rounded-lg border bg-card text-sm hover:border-primary/40 text-left">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  <span className="flex-1">{c}</span>
                  <ArrowRight className="h-3 w-3 text-muted-foreground self-center" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Templates in this workflow */}
      {templates.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-2">
            Templates for {workflow.label}
          </h2>
          <div className="grid md:grid-cols-2 gap-2">
            {templates.map((t) => (
              <button key={t.title} onClick={() => onTemplate(t.draft)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-card text-sm hover:border-primary/40 text-left">
                <ChevronRight className="h-3.5 w-3.5 text-primary" />
                <span className="flex-1">{t.title}</span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Use →</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VocabTag({ label, count }: { label: string; count: number }) {
  return (
    <span className="inline-flex items-center gap-1 bg-card border rounded-full px-2 py-0.5">
      <span className="text-foreground tabular-nums font-medium">{count}</span>
      <span>{label.toLowerCase()} live</span>
    </span>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Sentence canvas                                                         */
/* ────────────────────────────────────────────────────────────────────── */

function Keyword({ children }: { children: React.ReactNode }) {
  return <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground w-16 shrink-0 pt-1.5">{children}</span>;
}
function Chip({ children, onClick, filled = true }: any) {
  return (
    <button onClick={onClick}
      className={cn("inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-sm border",
        filled ? "bg-primary/10 border-primary/30 text-foreground hover:bg-primary/15"
               : "bg-transparent border-dashed border-muted-foreground/40 text-muted-foreground italic hover:border-foreground/60 hover:text-foreground")}>
      {children}
    </button>
  );
}

function SentenceCanvas({ draft, onEdit, onRemoveCondition, onRemoveAction, onClear }: {
  draft: PolicyDraft;
  onEdit: (clause: "when" | "if" | "then" | "unless" | "during") => void;
  onRemoveCondition: (i: number) => void;
  onRemoveAction: (i: number) => void;
  onClear: () => void;
}) {
  const wf = WORKFLOWS.find((w) => w.id === draft.workflowId);
  return (
    <div className="rounded-xl border bg-card p-6 space-y-3 relative">
      {wf && (
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
          <wf.icon className="h-3 w-3 text-primary" />
          <span>{wf.label} workflow</span>
        </div>
      )}
      <button onClick={onClear} className="absolute top-3 right-3 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground">
        Clear
      </button>

      <div className="flex items-start gap-3">
        <Keyword>WHEN</Keyword>
        <div className="flex flex-wrap gap-1.5">
          <Chip onClick={() => onEdit("when")}>{draft.triggerLabel ?? <span className="italic">choose an event</span>}</Chip>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <Keyword>IF</Keyword>
        <div className="flex flex-wrap gap-1.5 items-center">
          {draft.conditions.length === 0 && <Chip onClick={() => onEdit("if")} filled={false}>choose a condition…</Chip>}
          {draft.conditions.map((c, i) => (
            <span key={i} className="inline-flex items-center gap-1">
              {i > 0 && <span className="text-xs text-muted-foreground px-1">AND</span>}
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-sm border bg-primary/10 border-primary/30">
                <span>{c.label}</span>
                <span className="text-muted-foreground text-xs">{c.op}</span>
                {c.value !== "" && <span className="font-medium">{c.unit === "$" ? `$${c.value}` : c.unit ? `${c.value}${c.unit}` : c.value}</span>}
                <button onClick={() => onRemoveCondition(i)} className="ml-1 text-muted-foreground hover:text-destructive">
                  <X className="h-3 w-3" />
                </button>
              </span>
            </span>
          ))}
          {draft.conditions.length > 0 && (
            <button onClick={() => onEdit("if")} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5">
              <Plus className="h-3 w-3" /> and
            </button>
          )}
        </div>
      </div>

      <div className="flex items-start gap-3">
        <Keyword>THEN</Keyword>
        <div className="flex flex-col gap-1.5 flex-1">
          {draft.actions.length === 0 && <Chip onClick={() => onEdit("then")} filled={false}>choose an action…</Chip>}
          {draft.actions.map((a, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-xs text-muted-foreground pt-1 tabular-nums w-6">{String(i + 1).padStart(2, "0")}</span>
              <div className="inline-flex items-start gap-1 px-2.5 py-1 rounded-md text-sm border bg-primary/10 border-primary/30 flex-1">
                <div className="flex-1">
                  <div>{a.label}</div>
                  {Object.entries(a.params).length > 0 && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {Object.entries(a.params).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                    </div>
                  )}
                </div>
                <button onClick={() => onRemoveAction(i)} className="text-muted-foreground hover:text-destructive">
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
          {draft.actions.length > 0 && (
            <button onClick={() => onEdit("then")} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5 self-start">
              <Plus className="h-3 w-3" /> then...
            </button>
          )}
        </div>
      </div>

      <div className="flex items-start gap-3">
        <Keyword>UNLESS</Keyword>
        <Chip onClick={() => onEdit("unless")} filled={!!draft.unlessLabel}>
          {draft.unlessLabel ?? <span className="italic">optional exception</span>}
        </Chip>
      </div>
      <div className="flex items-start gap-3">
        <Keyword>DURING</Keyword>
        <Chip onClick={() => onEdit("during")} filled={!!draft.duringLabel}>
          {draft.duringLabel ?? <span className="italic">always</span>}
        </Chip>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Preview + explainability + footer + IR                                  */
/* ────────────────────────────────────────────────────────────────────── */

function PreviewPanel({ draft }: { draft: PolicyDraft }) {
  const seed = (draft.triggerId ?? "") + (draft.conditions[0]?.value ?? "");
  const fireCount = 12 + (seed.length * 7) % 60;
  const timeImpact = ((seed.length * 13) % 45) / 10;
  const dollarImpact = 250 + (seed.length * 137) % 3500;

  const mockMatches = [
    { ref: "ASN-8842", customer: "Vantiv Supply Co.", detail: "3.1% variance", when: "Nov 12" },
    { ref: "ASN-8801", customer: "Kirkland Naturals", detail: "5.7% variance", when: "Nov 09" },
    { ref: "ASN-8788", customer: "Vantiv Supply Co.", detail: "2.4% variance", when: "Nov 04" },
    { ref: "ASN-8712", customer: "Northbay Goods", detail: "4.2% variance", when: "Oct 31" },
  ];

  return (
    <div className="mt-4 rounded-xl border bg-card">
      <div className="px-5 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <History className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium uppercase tracking-wider">Backtest · last 30 days</span>
        </div>
        <button className="text-xs text-primary hover:underline flex items-center gap-1">
          <Play className="h-3 w-3" /> Test on a specific record
        </button>
      </div>
      <div className="grid grid-cols-3 gap-4 px-5 py-4 border-b">
        <Metric label="Would have fired" value={`${fireCount} times`} />
        <Metric label="Est. time impact" value={`+${timeImpact.toFixed(1)} hrs`} />
        <Metric label="Est. billing impact" value={`+$${dollarImpact.toLocaleString()}`} tone="positive" />
      </div>
      <div className="px-5 py-3 space-y-1.5">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Recent matches</div>
        {mockMatches.map((m) => (
          <div key={m.ref} className="grid grid-cols-[80px_1fr_120px_60px] gap-2 text-xs items-center py-1 hover:bg-muted/40 rounded px-1">
            <span className="font-mono text-muted-foreground">{m.ref}</span>
            <span>{m.customer}</span>
            <span className="text-muted-foreground">{m.detail}</span>
            <span className="text-muted-foreground text-right">{m.when}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: "positive" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn("text-xl font-semibold tabular-nums mt-0.5", tone === "positive" && "text-emerald-600")}>{value}</div>
    </div>
  );
}

function ExplainabilityPanel({ draft }: { draft: PolicyDraft }) {
  const trig = draft.triggerLabel ?? "the event";
  const cond = draft.conditions[0];
  const act = draft.actions[0];
  return (
    <div className="mt-4 rounded-xl border bg-card">
      <div className="px-5 py-3 border-b flex items-center gap-2">
        <Eye className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium uppercase tracking-wider">Every execution will be explainable</span>
      </div>
      <div className="p-5 grid md:grid-cols-2 gap-4 text-xs">
        <ExplainCard tone="pass" title="Why it executed"
          lines={[
            `Event: ${trig}`,
            cond ? `Matched: ${cond.label} ${cond.op} ${cond.value}${cond.unit ?? ""}` : "No conditions — always matches",
            act ? `Action: ${act.label}` : "No action selected",
            draft.duringLabel ? `Within: ${draft.duringLabel}` : "No time window",
          ]}
        />
        <ExplainCard tone="skip" title="Why it would skip"
          lines={[
            draft.unlessLabel ? `Exception: ${draft.unlessLabel}` : "No exception configured",
            "Trigger did not fire",
            cond ? `Condition failed: ${cond.label}` : "Condition set is empty",
            "Outside time window",
          ]}
        />
      </div>
      <div className="px-5 pb-4 text-[11px] text-muted-foreground flex items-center gap-1.5">
        <Info className="h-3 w-3" />
        Every decision writes an audit trail with the matched conditions and the reason it fired or skipped.
      </div>
    </div>
  );
}

function ExplainCard({ title, lines, tone }: { title: string; lines: string[]; tone: "pass" | "skip" }) {
  return (
    <div className={cn("rounded-lg border p-3",
      tone === "pass" ? "bg-emerald-500/5 border-emerald-500/20" : "bg-muted/40")}>
      <div className={cn("text-[10px] uppercase tracking-wider mb-1.5 font-semibold",
        tone === "pass" ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground")}>{title}</div>
      <ul className="space-y-1">
        {lines.map((l, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <span className={cn("mt-1.5 h-1 w-1 rounded-full shrink-0", tone === "pass" ? "bg-emerald-500" : "bg-muted-foreground/50")} />
            <span>{l}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FooterStrip() {
  return (
    <div className="mt-4 rounded-xl border bg-card px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
      <FooterField label="Scope" value="Tenant" />
      <FooterField label="Applies to" value="All customers" />
      <FooterField label="Priority" value="Normal" />
      <FooterField label="Effective" value="Immediately" />
      <FooterField label="Until" value="Forever" />
      <div className="flex items-center gap-1.5 ml-auto">
        <span className="text-muted-foreground">Test mode</span>
        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Off</span>
      </div>
    </div>
  );
}
function FooterField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-muted-foreground">{label}:</span>
      <button className="font-medium hover:underline">{value} ▾</button>
    </div>
  );
}

function IrPanel({ draft }: { draft: PolicyDraft }) {
  const ir = useMemo(() => ({
    trigger: { primitive: "Trigger", event: draft.triggerId },
    conditions: draft.conditions.map((c) => ({ primitive: "Condition", resolved_via: "EntityGraph → EntityAttributes", field: c.conditionId, op: c.op, value: c.value })),
    actions: draft.actions.map((a) => ({
      primitive: a.actionId.includes("photo") || a.actionId.includes("qc") ? "RequireAttestation"
                : a.actionId.includes("invoice") || a.actionId.includes("doc") ? "GenerateArtifact"
                : "Action",
      kind: a.actionId, params: a.params,
    })),
    binding: { primitive: "PolicyBinding", scope: "tenant" },
    during: draft.duringLabel ? { primitive: "TemporalContext", window: draft.duringLabel } : null,
  }), [draft]);
  return <pre className="mt-3 rounded-xl border bg-muted/30 p-4 text-[11px] font-mono overflow-x-auto">{JSON.stringify(ir, null, 2)}</pre>;
}

/* ────────────────────────────────────────────────────────────────────── */
/* Command palette — global search                                         */
/* ────────────────────────────────────────────────────────────────────── */

function CommandPalette({ onClose, onWorkflow, onTemplate, onTrigger }: {
  onClose: () => void;
  onWorkflow: (id: string) => void;
  onTemplate: (t: PolicyDraft) => void;
  onTrigger: (wid: string, t: { id: string; label: string }) => void;
}) {
  const [q, setQ] = useState("");
  const query = q.toLowerCase().trim();

  const workflowHits = WORKFLOWS.filter((w) =>
    !query || w.label.toLowerCase().includes(query) || w.tagline.toLowerCase().includes(query) || w.teaches.toLowerCase().includes(query)
  ).slice(0, 6);

  const triggerHits = WORKFLOWS.flatMap((w) =>
    w.triggers.filter((t) => !query || t.label.toLowerCase().includes(query) || t.description.toLowerCase().includes(query))
      .map((t) => ({ w, t }))
  ).slice(0, 8);

  const conditionHits = WORKFLOWS.flatMap((w) =>
    w.conditions.filter((c) => !query || c.label.toLowerCase().includes(query)).map((c) => ({ w, c }))
  ).slice(0, 6);

  const actionHits = WORKFLOWS.flatMap((w) =>
    w.actions.filter((a) => !query || a.label.toLowerCase().includes(query)).map((a) => ({ w, a }))
  ).slice(0, 6);

  const templateHits = TEMPLATES.filter((t) =>
    !query || t.title.toLowerCase().includes(query) || t.category.toLowerCase().includes(query)
  ).slice(0, 8);

  const promptHits = EXAMPLE_PROMPTS.filter((p) => !query || p.toLowerCase().includes(query)).slice(0, 4);

  return (
    <div className="fixed inset-0 z-50 bg-background/70 backdrop-blur-sm flex items-start justify-center p-6 pt-24" onClick={onClose}>
      <div className="rounded-xl border bg-card shadow-2xl w-[720px] max-w-full overflow-hidden flex flex-col" style={{ maxHeight: "70vh" }} onClick={(e) => e.stopPropagation()}>
        <div className="border-b px-4 py-3 flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            className="flex-1 bg-transparent text-sm outline-none"
            placeholder="Search warehouse concepts — sticker, storage, hazmat, carrier, variance, lot, gift, rush…" />
          <span className="text-[10px] font-mono border rounded px-1 py-0.5 bg-muted text-muted-foreground">ESC</span>
        </div>
        <div className="overflow-y-auto divide-y">
          {workflowHits.length > 0 && (
            <PaletteGroup title="Workflows">
              {workflowHits.map((w) => (
                <PaletteRow key={w.id} onSelect={() => onWorkflow(w.id)} icon={<w.icon className="h-3.5 w-3.5 text-primary" />}
                  label={w.label} hint={w.tagline} kind="Workflow" />
              ))}
            </PaletteGroup>
          )}
          {templateHits.length > 0 && (
            <PaletteGroup title="Templates">
              {templateHits.map((t) => (
                <PaletteRow key={t.title} onSelect={() => onTemplate(t.draft)}
                  icon={<Sparkles className="h-3.5 w-3.5 text-primary" />}
                  label={t.title} hint={t.category} kind="Template" />
              ))}
            </PaletteGroup>
          )}
          {triggerHits.length > 0 && (
            <PaletteGroup title="Events (triggers)">
              {triggerHits.map(({ w, t }) => (
                <PaletteRow key={w.id + t.id} onSelect={() => onTrigger(w.id, t)}
                  icon={<Zap className="h-3.5 w-3.5 text-primary" />}
                  label={t.label} hint={`${w.label} · ${t.description}`} kind={t.status === "soon" ? "Soon" : "Event"} />
              ))}
            </PaletteGroup>
          )}
          {conditionHits.length > 0 && (
            <PaletteGroup title="Conditions">
              {conditionHits.map(({ w, c }) => (
                <PaletteRow key={w.id + c.id} onSelect={() => onWorkflow(w.id)}
                  icon={<span className="text-xs text-primary">IF</span>}
                  label={c.label} hint={w.label} kind="Condition" />
              ))}
            </PaletteGroup>
          )}
          {actionHits.length > 0 && (
            <PaletteGroup title="Actions">
              {actionHits.map(({ w, a }) => (
                <PaletteRow key={w.id + a.id} onSelect={() => onWorkflow(w.id)}
                  icon={<span className="text-xs text-primary">DO</span>}
                  label={a.label} hint={w.label} kind="Action" />
              ))}
            </PaletteGroup>
          )}
          {promptHits.length > 0 && (
            <PaletteGroup title="Example policies">
              {promptHits.map((p) => (
                <PaletteRow key={p} onSelect={onClose}
                  icon={<Lightbulb className="h-3.5 w-3.5 text-amber-500" />}
                  label={p} hint="Ask CairnOS to draft this" kind="Prompt" />
              ))}
            </PaletteGroup>
          )}
          {workflowHits.length + templateHits.length + triggerHits.length + conditionHits.length + actionHits.length + promptHits.length === 0 && (
            <div className="p-8 text-sm text-muted-foreground text-center">
              Nothing found for "{q}". Try: sticker, storage, hazmat, variance, cutoff.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
function PaletteGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="py-1">
      <div className="px-4 pt-2 pb-1 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}
function PaletteRow({ icon, label, hint, kind, onSelect }: { icon: React.ReactNode; label: string; hint: string; kind: string; onSelect: () => void }) {
  return (
    <button onClick={onSelect} className="w-full flex items-center gap-3 px-4 py-2 hover:bg-accent text-left">
      <span className="h-5 w-5 rounded flex items-center justify-center bg-primary/10">{icon}</span>
      <span className="flex-1 min-w-0">
        <div className="text-sm truncate">{label}</div>
        <div className="text-[11px] text-muted-foreground truncate">{hint}</div>
      </span>
      <span className="text-[9px] uppercase tracking-wider text-muted-foreground border rounded px-1.5 py-0.5">{kind}</span>
    </button>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Pickers                                                                 */
/* ────────────────────────────────────────────────────────────────────── */

function PickerShell({ title, onClose, children, wide = true }: any) {
  return (
    <div className="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
      <div className={cn("rounded-xl border bg-card shadow-2xl overflow-hidden flex flex-col", wide ? "w-[820px] max-w-full" : "w-[520px]")} style={{ maxHeight: "80vh" }} onClick={(e) => e.stopPropagation()}>
        <div className="h-11 border-b flex items-center justify-between px-4 shrink-0">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</span>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function TriggerPicker({ workflowId, setWorkflow, search, setSearch, onPick, onClose }: any) {
  const workflow = WORKFLOWS.find((w) => w.id === workflowId)!;
  const [hovered, setHovered] = useState(workflow.triggers[0] ?? { label: "", description: "", status: "live" });
  const matches = search
    ? WORKFLOWS.flatMap((w) => w.triggers.filter((t) => t.label.toLowerCase().includes(search.toLowerCase())).map((t) => ({ w, t })))
    : null;
  return (
    <PickerShell title="WHEN" onClose={onClose}>
      <div className="px-4 py-2 border-b">
        <div className="relative">
          <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 rounded border bg-transparent text-sm outline-none focus:ring-1 focus:ring-primary/40"
            placeholder="Search across all workflows…" />
        </div>
      </div>
      {matches ? (
        <div className="overflow-y-auto flex-1">
          {matches.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No events match "{search}"</div>}
          {matches.map(({ w, t }) => (
            <button key={w.id + t.id} disabled={t.status === "soon"} onClick={() => onPick(w.id, t)}
              className="w-full flex items-center gap-3 px-4 py-2 hover:bg-accent text-left disabled:opacity-50">
              <span className={cn("h-2 w-2 rounded-full", t.status === "live" ? "bg-primary" : "bg-muted-foreground/40 border")} />
              <span className="text-sm">{t.label}</span>
              <span className="text-xs text-muted-foreground ml-auto">{w.label}</span>
              {t.status === "soon" && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Soon</span>}
            </button>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-[200px_1fr_240px] flex-1 min-h-0">
          <div className="border-r overflow-y-auto py-1">
            {WORKFLOWS.map((w) => (
              <button key={w.id} onClick={() => setWorkflow(w.id)}
                className={cn("w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-accent",
                  workflowId === w.id && "bg-accent")}>
                <span className={cn("h-1.5 w-1.5 rounded-full", workflowId === w.id ? "bg-primary" : "bg-muted-foreground/30")} />
                {w.label}
              </button>
            ))}
          </div>
          <div className="overflow-y-auto py-1">
            {workflow.triggers.map((t) => (
              <button key={t.id} disabled={t.status === "soon"}
                onMouseEnter={() => setHovered(t)} onClick={() => onPick(workflow.id, t)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent text-left disabled:opacity-50">
                <span className={cn("h-2 w-2 rounded-full shrink-0", t.status === "live" ? "bg-primary" : "bg-muted-foreground/40 border")} />
                <span className="flex-1">{t.label}</span>
                {t.status === "soon" && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Soon</span>}
              </button>
            ))}
            {workflow.triggers.length === 0 && (
              <div className="px-4 py-6 text-xs text-muted-foreground italic">No events wired yet for this workflow.</div>
            )}
          </div>
          <div className="border-l p-4 bg-muted/20 overflow-y-auto">
            <div className="text-xs font-semibold mb-1">{hovered.label}</div>
            <div className="text-xs text-muted-foreground leading-relaxed">{hovered.description}</div>
            {hovered.status === "soon" && (
              <div className="mt-3 text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-amber-500/10 text-amber-600 inline-block">Coming soon</div>
            )}
          </div>
        </div>
      )}
    </PickerShell>
  );
}

function ConditionPicker({ workflowId, onPick, onClose }: any) {
  const workflow = WORKFLOWS.find((w) => w.id === workflowId)!;
  const inScope = workflow.conditions.filter((c) => c.tier === "default");
  const more = workflow.conditions.filter((c) => c.tier !== "default");
  const [selected, setSelected] = useState<any>(inScope[0] ?? SHARED_CONDITIONS[0]);
  const [op, setOp] = useState("");
  const [value, setValue] = useState("");
  const opsFor = (type: string) => {
    if (type === "number") return ["is greater than", "is at least", "is less than", "equals", "is between"];
    if (type === "text")   return ["is", "is not", "is one of", "contains"];
    if (type === "list")   return ["includes", "does not include", "includes any of"];
    if (type === "boolean") return ["is true", "is false"];
    return ["equals"];
  };
  const currentOps = opsFor(selected.type);
  return (
    <PickerShell title={`IF · ${workflow.label} in scope`} onClose={onClose}>
      <div className="grid grid-cols-[220px_1fr] flex-1 min-h-0">
        <div className="border-r overflow-y-auto py-2">
          <div className="px-3 pb-1 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">In {workflow.label}</div>
          {inScope.map((c) => (
            <button key={c.id} onClick={() => { setSelected(c); setOp(""); setValue(""); }}
              className={cn("w-full text-left px-3 py-1.5 text-sm hover:bg-accent flex items-center gap-2",
                selected.id === c.id && "bg-accent")}>
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              {c.label}
            </button>
          ))}
          {inScope.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground italic">No workflow-native conditions yet.</div>}
          <div className="px-3 pt-3 pb-1 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">Shared</div>
          {SHARED_CONDITIONS.map((c) => (
            <button key={c.id} onClick={() => { setSelected(c); setOp(""); setValue(""); }}
              className={cn("w-full text-left px-3 py-1.5 text-sm hover:bg-accent flex items-center gap-2",
                selected.id === c.id && "bg-accent")}>
              <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />
              {c.label}
            </button>
          ))}
          {more.length > 0 && (
            <>
              <div className="px-3 pt-3 pb-1 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">More</div>
              {more.map((c) => (
                <button key={c.id} onClick={() => { setSelected(c); setOp(""); setValue(""); }}
                  className={cn("w-full text-left px-3 py-1.5 text-sm hover:bg-accent flex items-center gap-2 opacity-70 hover:opacity-100",
                    selected.id === c.id && "bg-accent opacity-100")}>
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                  {c.label}
                </button>
              ))}
            </>
          )}
        </div>
        <div className="p-5 overflow-y-auto space-y-3">
          <div className="text-sm font-medium">{selected.label}</div>
          <div className="grid grid-cols-[1fr_1fr] gap-2">
            <select value={op} onChange={(e) => setOp(e.target.value)} className="px-2 py-1.5 rounded border bg-transparent text-sm">
              <option value="">choose…</option>
              {currentOps.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            {selected.type === "boolean" ? (
              <div className="text-xs text-muted-foreground pt-1.5">no value needed</div>
            ) : (
              <div className="flex items-center gap-1">
                {selected.unit === "$" && <span className="text-sm text-muted-foreground">$</span>}
                <input value={value} onChange={(e) => setValue(e.target.value)}
                  placeholder={selected.type === "number" ? "0" : "value"}
                  className="flex-1 px-2 py-1.5 rounded border bg-transparent text-sm outline-none focus:ring-1 focus:ring-primary/40" />
                {selected.unit && selected.unit !== "$" && <span className="text-sm text-muted-foreground">{selected.unit}</span>}
              </div>
            )}
          </div>
          <div className="rounded-lg border bg-muted/30 p-3 text-xs">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Examples in last 30 days</div>
            <div className="space-y-0.5 text-muted-foreground">
              <div>▸ ASN-8842 (Vantiv) — 3.1%</div>
              <div>▸ ASN-8801 (Kirkland) — 5.7%</div>
              <div>▸ ASN-8788 (Vantiv) — 2.4%</div>
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <button
              disabled={!op || (selected.type !== "boolean" && !value)}
              onClick={() => onPick({ workflowId, conditionId: selected.id, label: selected.label, op, value, unit: selected.unit })}
              className="text-xs px-3 py-1.5 rounded bg-primary text-primary-foreground disabled:opacity-40">
              Add condition
            </button>
          </div>
        </div>
      </div>
    </PickerShell>
  );
}

function ActionPicker({ workflowId, onPick, onClose }: any) {
  const workflow = WORKFLOWS.find((w) => w.id === workflowId)!;
  const inScope = workflow.actions.filter((a) => a.tier === "default");
  const [selected, setSelected] = useState<any>(inScope[0] ?? { id: "shared.notify", label: "Send a notification", params: [] });
  const [params, setParams] = useState<Record<string, string>>({});
  return (
    <PickerShell title={`THEN · ${workflow.label} actions`} onClose={onClose}>
      <div className="grid grid-cols-[220px_1fr] flex-1 min-h-0">
        <div className="border-r overflow-y-auto py-2">
          <div className="px-3 pb-1 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">In {workflow.label}</div>
          {inScope.map((a) => (
            <button key={a.id} disabled={a.status === "soon"} onClick={() => { setSelected(a); setParams({}); }}
              className={cn("w-full text-left px-3 py-1.5 text-sm hover:bg-accent flex items-center gap-2 disabled:opacity-50",
                selected.id === a.id && "bg-accent")}>
              <span className={cn("h-1.5 w-1.5 rounded-full", a.status === "live" ? "bg-primary" : "bg-muted-foreground/40 border")} />
              {a.label}
              {a.status === "soon" && <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-muted text-muted-foreground ml-auto">Soon</span>}
            </button>
          ))}
          {inScope.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground italic">No workflow-native actions yet.</div>}
          <div className="px-3 pt-3 pb-1 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">Shared</div>
          {SHARED_ACTIONS.map((a) => (
            <button key={a.id} onClick={() => { setSelected({ ...a, params: [] }); setParams({}); }}
              className={cn("w-full text-left px-3 py-1.5 text-sm hover:bg-accent flex items-center gap-2",
                selected.id === a.id && "bg-accent")}>
              <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />
              {a.label}
            </button>
          ))}
        </div>
        <div className="p-5 overflow-y-auto space-y-3">
          <div className="text-sm font-medium">{selected.label}</div>
          {(selected.params ?? []).length === 0 ? (
            <div className="text-xs text-muted-foreground">No configuration needed.</div>
          ) : (
            <div className="grid gap-2">
              {selected.params.map((p: any) => (
                <div key={p.name}>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">{p.label}</label>
                  <input value={params[p.name] ?? ""} onChange={(e) => setParams({ ...params, [p.name]: e.target.value })}
                    className="w-full mt-0.5 px-2 py-1.5 rounded border bg-transparent text-sm outline-none focus:ring-1 focus:ring-primary/40" />
                </div>
              ))}
            </div>
          )}
          <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground flex gap-2">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>Actions run in order. You can chain more with "then..." after adding this one.</span>
          </div>
          <div className="flex justify-end pt-2">
            <button onClick={() => onPick({ workflowId, actionId: selected.id, label: selected.label, params })}
              className="text-xs px-3 py-1.5 rounded bg-primary text-primary-foreground">
              Add action
            </button>
          </div>
        </div>
      </div>
    </PickerShell>
  );
}

function SimpleTextPicker({ title, placeholder, onPick, onClose }: any) {
  const [v, setV] = useState("");
  return (
    <PickerShell title={title} onClose={onClose} wide={false}>
      <div className="p-5 space-y-3">
        <input value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder}
          className="w-full px-3 py-2 rounded border bg-transparent text-sm outline-none focus:ring-1 focus:ring-primary/40" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded border">Cancel</button>
          <button onClick={() => onPick(v)} disabled={!v.trim()}
            className="text-xs px-3 py-1.5 rounded bg-primary text-primary-foreground disabled:opacity-40">Set</button>
        </div>
      </div>
    </PickerShell>
  );
}